import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Fragment, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router';
import { Button, Card, Field, FormAction, FormGrid, SelectField } from '@crave/ui';
import { api, apiBaseUrl } from '../api.ts';
import { LOCATION_TIMEZONE, Notice, Section, errorText, money, today } from '../components/ui.tsx';
import { getAccessToken, refreshAccessToken } from '../lib/auth.ts';
import { useStaff } from '../lib/staff.tsx';

// ------------------------------------------------------------------ API shapes (routes/admin/audit.ts)

export interface AuditActor {
  type: string;
  id: string | null;
  name: string | null;
  email: string | null;
  roles: string[];
}

export interface AuditItem {
  id: string;
  kind: 'change' | 'request';
  created_at: string;
  /** New York wall-clock time, "YYYY-MM-DD HH:MM:SS". */
  local_time: string;
  actor: AuditActor;
  app: string | null;
  ip: string | null;
  user_agent: string | null;
  aal: string | null;
  request_id: string | null;
  action: string;
  entity: { type: string; id: string | null; label: string | null; noun: string | null } | null;
  summary: string | null;
  reason: string | null;
  changed_fields: Record<string, { from?: unknown; to?: unknown }> | null;
  before: unknown;
  after: unknown;
  location_id: string | null;
  request: {
    method: string | null;
    path: string | null;
    route: string | null;
    status: number | null;
    error_code: string | null;
    duration_ms: number | null;
  } | null;
}

interface AuditPage {
  items: AuditItem[];
  next_cursor: string | null;
}

interface Facets {
  actors: {
    id: string;
    name: string | null;
    email: string | null;
    roles: string[];
    count: number;
    on_staff: boolean;
  }[];
  actor_types: { type: string; count: number }[];
  entity_types: { type: string; label: string | null; count: number }[];
  actions: { action: string; count: number }[];
  apps: { app: string; count: number }[];
  refused_requests: number;
}

interface RequestRow {
  id: string;
  request_id: string;
  created_at: string;
  actor_type: string;
  aal: string | null;
  ip: string | null;
  user_agent: string | null;
  origin: string | null;
  app: string | null;
  method: string;
  path: string;
  route: string | null;
  query: unknown;
  body: unknown;
  status: number;
  error_code: string | null;
  duration_ms: number | null;
}

interface AuditDetail {
  item: AuditItem;
  request: RequestRow | null;
  related: {
    id: string;
    created_at: string;
    action: string;
    entity_type: string;
    entity_id: string | null;
    entity_label: string | null;
    summary: string | null;
  }[];
}

// ------------------------------------------------------------------ plain words

const ROLE_NAMES: Record<string, string> = {
  owner: 'Owner',
  manager: 'Manager',
  team_member: 'Kitchen & counter',
  kitchen: 'Kitchen',
  cashier: 'Front counter',
};

const APP_NAMES: Record<string, string> = {
  admin: 'Admin portal',
  kitchen: 'Kitchen app',
  order: 'Ordering site',
  api: 'Directly to the server',
};
const APP_BADGES: Record<string, string> = {
  admin: 'Admin',
  kitchen: 'Kitchen',
  order: 'Ordering site',
  api: 'Server',
};

const ACTOR_TYPE_NAMES: Record<string, string> = {
  CUSTOMER: 'Customers',
  SYSTEM: 'Automatic (Crave itself)',
  WEBHOOK: 'Outside services (payments, email)',
  ANONYMOUS: 'Someone not signed in',
};

const ACTION_NAMES: Record<string, string> = {
  insert: 'Created',
  update: 'Changed',
  delete: 'Deleted',
  'request.refused': 'Refused attempt',
  'request.failed': 'Failed attempt',
  'category.delete': 'Deleted a menu category',
  'comp_budget.delete': 'Deleted a comp budget',
  'customer.delete': 'Deleted a customer',
  'customer.erase': 'Erased a customer’s personal details',
  'feedback.delete': 'Deleted feedback',
  'ingredient.delete': 'Deleted an ingredient',
  'ingredient.package.add': 'Added an ingredient package',
  'ingredient.package.remove': 'Removed an ingredient package',
  'integration.resend.connect': 'Connected email sending',
  'integration.resend.disconnect': 'Disconnected email sending',
  'integration.resend.test': 'Sent a test email',
  'integration.stripe.connect': 'Connected Stripe payments',
  'integration.stripe.connect.failed': 'Stripe connection failed',
  'integration.stripe.disconnect': 'Disconnected Stripe payments',
  'inventory.adjust.add': 'Added stock by hand',
  'inventory.adjust.remove': 'Removed stock by hand',
  'inventory.adjust.set': 'Set a stock count',
  'location.update': 'Changed location settings',
  'menu_item.delete': 'Deleted a menu item',
  'modifier.delete': 'Deleted a topping or option',
  'order.capture': 'Charged a card',
  'order.capture.failed': 'Card charge failed',
  'order.comp': 'Comped an order',
  'order.note': 'Added an order note',
  'order.override': 'Changed an order status by hand',
  'order.refund': 'Refunded an order',
  'order.refund.failed': 'Refund failed',
  'ordering.disabled': 'Turned online ordering off',
  'ordering.enabled': 'Turned online ordering on',
  'ordering.paused': 'Paused ordering',
  'ordering.paused_message': 'Changed the paused message',
  'ordering.resumed': 'Resumed ordering',
  'price.confirm': 'Confirmed a price',
  'receipt.delete': 'Deleted a draft receipt',
  'receipt.post': 'Posted a delivery receipt',
  'receipt.void': 'Voided a delivery receipt',
  'recipe.delete': 'Deleted a recipe',
  'recipe.draft.discard': 'Discarded a recipe draft',
  'recipe.publish': 'Published a recipe',
  'schedule.update': 'Changed the weekly hours',
  'special_day.remove': 'Removed a special day',
  'staff.delete': 'Deleted a staff member',
  'staff.login.create': 'Created a staff login',
  'staff.login.link': 'Linked a staff login',
  'staff.login.reset': 'Reset a staff login',
  'staff.roles.set': 'Changed staff roles',
  'tasting.batch.delete': 'Deleted a tasting batch',
  'tasting.delete': 'Deleted a tasting',
  'vendor.delete': 'Deleted a vendor',
};

/** What an API refusal meant, in words. */
const ERROR_WORDS: Record<string, string> = {
  VALIDATION_ERROR: 'something that was typed was not accepted',
  UNAUTHENTICATED: 'the person was not signed in',
  FORBIDDEN: 'this role is not allowed to do that',
  MFA_REQUIRED: 'a two-factor sign-in was needed',
  NOT_FOUND: 'the record was not found',
  VERSION_CONFLICT: 'someone else changed it first',
  IDEMPOTENCY_CONFLICT: 'the same request was already running',
  IDEMPOTENCY_KEY_REQUIRED: 'the app sent an incomplete request',
  IDEMPOTENCY_KEY_REUSED: 'the app sent the same request twice',
  DELETE_BLOCKED: 'the record is still in use, so it can’t be deleted',
  INVALID_TRANSITION: 'that status change is not allowed',
  ORDER_NOT_CANCELABLE: 'the order can no longer be canceled',
  RATE_LIMITED: 'too many tries in a short time',
  INSUFFICIENT_INVENTORY: 'there was not enough stock',
  PAYMENT_FAILED: 'the payment did not go through',
  PAYMENTS_NOT_CONNECTED: 'payments are not connected',
  PAYMENT_PROVIDER_UNAVAILABLE: 'the payment service did not answer',
  PRICE_NOT_SET: 'a price is missing',
  ORDERING_DISABLED: 'online ordering is off',
  ORDERING_PAUSED: 'ordering is paused',
  SLOT_FULL: 'that pickup time is full',
  SLOT_TOO_SOON: 'that pickup time is too soon',
  SLOT_UNAVAILABLE: 'that pickup time is not available',
  ITEM_UNAVAILABLE: 'an item is not available',
  CREDIT_INSUFFICIENT: 'there was not enough Crave Credit',
  BIRTHDAY_ALREADY_USED: 'the birthday reward was already used',
  ALREADY_SUBMITTED: 'it was already sent',
  FEEDBACK_NOT_OPEN: 'feedback is not open',
  TASTING_CLOSED: 'the tasting is closed',
  INTERNAL: 'the server had a problem',
};

const METHOD_WORDS: Record<string, string> = {
  POST: 'save',
  PUT: 'change',
  PATCH: 'change',
  DELETE: 'delete',
};

/** Words the tidy-up leaves in capitals. */
const KEEP_CAPS = new Set(['CSV', 'MFA', 'USD', 'API', 'URL', 'PDF', 'FIFO', 'BBQ']);

const capitalize = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const humanize = (s: string) => capitalize(s.replace(/[._-]+/g, ' ').trim());

/** "ingredient_packages" as a noun when the API did not send one. */
function nounFor(type: string | null | undefined): string {
  if (!type) return 'record';
  const t = type.replace(/_/g, ' ');
  if (t.endsWith('ies')) return `${t.slice(0, -3)}y`;
  if (/(ch|sh|x|ss)es$/.test(t)) return t.slice(0, -2);
  if (t.endsWith('s') && !t.endsWith('ss')) return t.slice(0, -1);
  return t;
}

export function actionLabel(action: string): string {
  return ACTION_NAMES[action] ?? humanize(action);
}

export function roleLabel(roles: readonly string[] | null | undefined): string {
  return (roles ?? []).map((r) => ROLE_NAMES[r] ?? humanize(r)).join(', ');
}

/** "Frank Torres, Owner" or "Automatic". */
export function personLabel(actor: AuditActor): string {
  if (actor.type === 'STAFF') {
    const name = actor.name ?? actor.email ?? 'A staff member';
    const roles = roleLabel(actor.roles);
    return roles ? `${name}, ${roles}` : name;
  }
  if (actor.type === 'CUSTOMER') return actor.email ? `Customer ${actor.email}` : 'A customer';
  if (actor.type === 'SYSTEM') return 'Automatic';
  if (actor.type === 'WEBHOOK') return 'Outside service';
  if (actor.type === 'ANONYMOUS') return 'Someone not signed in';
  return humanize(actor.type.toLowerCase());
}

const DAY_FORMAT = new Intl.DateTimeFormat('en-US', {
  weekday: 'long',
  month: 'long',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
});
const SHORT_DAY_FORMAT = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
});
const INSTANT_FORMAT = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  timeZone: LOCATION_TIMEZONE,
});
const INSTANT_SECONDS_FORMAT = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  second: '2-digit',
  timeZone: LOCATION_TIMEZONE,
});

/** A calendar date ("YYYY-MM-DD") moved by whole days. */
function addDays(date: string, days: number): string {
  const d = new Date(`${date}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function dayHeading(date: string, todayDate: string): string {
  const d = new Date(`${date}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return date;
  const label = DAY_FORMAT.format(d);
  if (date === todayDate) return `Today, ${label}`;
  if (date === addDays(todayDate, -1)) return `Yesterday, ${label}`;
  return label;
}

/** "8:32 PM" from the API's New York wall-clock time. */
export function clockTime(localTime: string): string {
  const h = Number(localTime.slice(11, 13));
  const m = localTime.slice(14, 16);
  if (!Number.isFinite(h) || m.length !== 2) return localTime;
  return `${h % 12 || 12}:${m} ${h >= 12 ? 'PM' : 'AM'}`;
}

/** "Today 8:32 PM", "Yesterday 8:32 PM" or "Sep 12, 2026 8:32 PM". */
export function shortWhen(localTime: string, todayDate = today()): string {
  const date = localTime.slice(0, 10);
  const time = clockTime(localTime);
  if (date === todayDate) return `Today ${time}`;
  if (date === addDays(todayDate, -1)) return `Yesterday ${time}`;
  const d = new Date(`${date}T12:00:00Z`);
  return Number.isNaN(d.getTime()) ? localTime : `${SHORT_DAY_FORMAT.format(d)}, ${time}`;
}

const ISO_INSTANT = /\b\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?/g;
const ISO_DATE = /\b\d{4}-\d{2}-\d{2}\b/g;
const CODE_WORD = /\b[A-Z][A-Z0-9]*(?:_[A-Z0-9]+)+\b|\b[A-Z]{3,}\b/g;

function formatInstant(text: string, seconds = false): string {
  const d = new Date(text);
  if (Number.isNaN(d.getTime())) return text;
  return (seconds ? INSTANT_SECONDS_FORMAT : INSTANT_FORMAT).format(d);
}

function formatDate(text: string): string {
  const d = new Date(`${text}T12:00:00Z`);
  return Number.isNaN(d.getTime()) ? text : SHORT_DAY_FORMAT.format(d);
}

/**
 * A stored sentence in plain words: timestamps as Florida time, codes such as MANUAL_REVIEW as
 * "manual review". Names inside “curly quotes” are left exactly as typed.
 */
export function tidyText(text: string): string {
  return text
    .replace(/^Changed is active of (.+) from true to false\.$/, 'Deactivated $1.')
    .replace(/^Changed is active of (.+) from false to true\.$/, 'Reactivated $1.')
    .split(/(“[^”]*”)/)
    .map((part) =>
      part.startsWith('“')
        ? part
        : part
            .replace(ISO_INSTANT, (m) => formatInstant(m))
            .replace(ISO_DATE, (m) => formatDate(m))
            .replace(/\b\d+\.\d{3,}\b/g, (m) =>
              Number(m).toLocaleString('en-US', { maximumFractionDigits: 2 }),
            )
            .replace(/\bqty\b/g, 'quantity')
            .replace(/\beach weight g\b/g, 'weight of one (grams)')
            .replace(/\bdensity g per ml\b/g, 'density (grams per ml)')
            .replace(/\bstripe\b/g, 'Stripe')
            .replace(
              /\bfrom (true|false) to (true|false)\b/g,
              (_m, a: string, b: string) =>
                `from ${a === 'true' ? 'on' : 'off'} to ${b === 'true' ? 'on' : 'off'}`,
            )
            .replace(/\b(from|to) empty\b/g, '$1 nothing')
            .replace(/\bpct\b/g, 'percent')
            .replace(CODE_WORD, (m) => (KEEP_CAPS.has(m) ? m : m.replace(/_/g, ' ').toLowerCase())),
    )
    .join('');
}

/** "recipes › ingredients" from a route such as /v1/admin/recipes/ingredients/:id. */
function areaOf(route: string | null, path: string | null): string {
  const parts = (route ?? path ?? '')
    .split('/')
    .filter(
      (p) =>
        p &&
        p !== 'v1' &&
        !p.startsWith(':') &&
        !/[{}*]/.test(p) &&
        !/^\d+$/.test(p) &&
        !/^[0-9a-f]{8}-[0-9a-f-]{27,}$/i.test(p),
    );
  const scope = parts[0] ?? '';
  const rest = parts.slice(1).map((p) => p.replace(/[-_]/g, ' '));
  if (scope === 'me') rest.unshift('customer account');
  else if (scope === 'track') rest.unshift('order tracking');
  else if (scope === 'staff') rest.unshift('kitchen');
  else if (scope !== 'admin' && scope) rest.unshift(scope.replace(/[-_]/g, ' '));
  return rest.length ? rest.join(' › ') : 'Crave';
}

function errorWords(status: number | null, code: string | null): string {
  if (code && ERROR_WORDS[code]) return ERROR_WORDS[code];
  if (status !== null && status >= 500) return 'the server had a problem';
  if (status === 401) return 'the person was not signed in';
  if (status === 403) return 'this role is not allowed to do that';
  if (status === 404) return 'the record was not found';
  if (status === 409) return 'it conflicted with the record as it is now';
  if (status === 422) return 'something that was typed was not accepted';
  return code ? humanize(code.toLowerCase()) : 'it was not accepted';
}

/** One plain sentence for an entry. */
export function plainSummary(item: AuditItem): string {
  const r = item.request;
  if (item.kind === 'request' && r) {
    const failed = (r.status ?? 0) >= 500;
    const verb = METHOD_WORDS[(r.method ?? '').toUpperCase()] ?? 'change';
    return `${failed ? 'Failed' : 'Refused'}: ${verb} in ${areaOf(r.route, r.path)}. ${capitalize(errorWords(r.status, r.error_code))}.`;
  }
  const keys = item.changed_fields ? Object.keys(item.changed_fields) : [];
  if (
    item.entity?.type === 'app_users' &&
    item.action === 'update' &&
    keys.length === 1 &&
    keys[0] === 'last_login_at'
  ) {
    return `Recorded a sign-in by ${item.entity.label ? `“${item.entity.label}”` : 'a staff member'}.`;
  }
  const name = recordName(item);
  const stored = item.entity?.label ?? null;
  if (item.summary) {
    const text =
      name && stored && name !== stored
        ? item.summary.split(`“${stored}”`).join(`“${name}”`)
        : item.summary;
    return tidyText(text);
  }
  const noun = item.entity?.noun ?? nounFor(item.entity?.type);
  return `${actionLabel(item.action)}: ${noun}${name ? ` “${name}”` : ''}.`;
}

const SLOT_FORMAT = new Intl.DateTimeFormat('en-US', {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  timeZone: LOCATION_TIMEZONE,
});

/** The record's name as shown. A pickup slot is named by its start time ("Tue, Sep 15, 9:30 AM"). */
export function recordName(item: AuditItem): string | null {
  const e = item.entity;
  if (!e) return null;
  if (e.type === 'pickup_slots') {
    const data = isRecord(item.after) ? item.after : isRecord(item.before) ? item.before : null;
    const start = data?.['slot_start'];
    if (typeof start === 'string') {
      const d = new Date(start);
      if (!Number.isNaN(d.getTime())) return SLOT_FORMAT.format(d);
    }
  }
  return e.label;
}

/** "Safari on an iPhone" from a user agent. */
export function describeDevice(ua: string | null | undefined): string {
  if (!ua) return 'Not recorded';
  if (/^curl\//i.test(ua)) return 'A command-line tool (curl)';
  if (/Stripe/i.test(ua)) return 'Stripe';
  if (/^(node|undici)/i.test(ua) || /node-fetch|axios/i.test(ua)) return 'A server script';
  if (/Playwright|HeadlessChrome/i.test(ua)) return 'An automated test browser';
  const device = /iPhone/.test(ua)
    ? 'an iPhone'
    : /iPad/.test(ua)
      ? 'an iPad'
      : /Android/.test(ua)
        ? /Mobile/.test(ua)
          ? 'an Android phone'
          : 'an Android tablet'
        : /CrOS/.test(ua)
          ? 'a Chromebook'
          : /Macintosh|Mac OS X/.test(ua)
            ? 'a Mac'
            : /Windows/.test(ua)
              ? 'a Windows computer'
              : /Linux/.test(ua)
                ? 'a Linux computer'
                : null;
  const browser = /Edg(e|A|iOS)?\//.test(ua)
    ? 'Edge'
    : /OPR\/|Opera/.test(ua)
      ? 'Opera'
      : /Firefox\/|FxiOS\//.test(ua)
        ? 'Firefox'
        : /CriOS\/|Chrome\//.test(ua)
          ? 'Chrome'
          : /Safari\//.test(ua)
            ? 'Safari'
            : /AppleWebKit/.test(ua) && device
              ? 'An app'
              : null;
  if (browser && device) return `${browser} on ${device}`;
  if (browser) return browser;
  if (device) return `A browser on ${device}`;
  return 'Unknown device';
}

function durationText(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return 'Not recorded';
  if (ms < 1000) return `${ms} milliseconds`;
  return `${(ms / 1000).toFixed(1)} seconds`;
}

function resultText(status: number | null | undefined, code: string | null | undefined): string {
  if (status === null || status === undefined) return 'Not recorded';
  if (status < 300) return `Worked (${status})`;
  if (status < 400) return `Redirected (${status})`;
  return `${status >= 500 ? 'Failed' : 'Refused'}: ${errorWords(status, code ?? null)} (${status})`;
}

/** Where the record lives in the admin, when a page for it exists. */
function recordLink(item: AuditItem): { to: string; text: string } | null {
  const e = item.entity;
  if (!e) return null;
  const data = (item.after ?? item.before) as Record<string, unknown> | null;
  const field = (k: string) => {
    const v = data && typeof data === 'object' ? data[k] : null;
    return typeof v === 'string' ? v : null;
  };
  const gone = item.action === 'delete' || /\.delete$|\.remove$/.test(item.action);
  const detail = (to: string, noun: string) => (gone ? null : { to, text: `Open ${noun}` });
  const list = (to: string, page: string) => ({ to, text: `Go to ${page}` });
  switch (e.type) {
    case 'orders':
      return e.id ? detail(`/orders/${e.id}`, 'order') : null;
    case 'payments':
    case 'refunds': {
      const order = field('order_id');
      return order ? { to: `/orders/${order}`, text: 'Open order' } : null;
    }
    case 'customers':
      return e.id && item.action !== 'customer.erase'
        ? detail(`/customers/${e.id}`, 'customer')
        : null;
    case 'loyalty_accounts':
      return e.id ? { to: `/customers/${e.id}`, text: 'Open customer' } : null;
    case 'tasting_campaigns':
      return e.id
        ? (detail(`/tastings/${e.id}`, 'tasting') ?? list('/tastings', 'Tastings'))
        : null;
    case 'tasting_batches':
    case 'tasting_samples': {
      const campaign = field('campaign_id');
      return campaign
        ? { to: `/tastings/${campaign}`, text: 'Open tasting' }
        : list('/tastings', 'Tastings');
    }
    case 'feedback':
      return list('/feedback', 'Feedback');
    case 'ingredients':
    case 'ingredient_packages':
      return list('/ingredients', 'Ingredients');
    case 'recipes':
    case 'recipe_versions':
    case 'recipe_version_ingredients':
      return list('/recipes', 'Recipes');
    case 'app_users':
    case 'user_roles':
      return list('/staff', 'Staff');
    case 'vendors':
    case 'purchase_receipts':
    case 'receipt_items':
    case 'planned_purchases':
      return list('/purchasing', 'Purchasing');
    case 'inventory_lots':
    case 'inventory_stock':
    case 'physical_counts':
    case 'physical_count_lines':
    case 'production_runs':
      return list('/inventory', 'Inventory');
    case 'menu_items':
    case 'menu_item_prices':
    case 'menu_item_modifiers':
    case 'location_menu_items':
    case 'categories':
    case 'modifiers':
    case 'locations':
      return list('/readiness', 'Menu, prices & hours');
    case 'service_schedules':
    case 'service_date_overrides':
    case 'pickup_slots':
      return list('/capacity', 'Capacity');
    case 'comp_budgets':
    case 'unit_economics_assumptions':
      return list('/reports', 'Reports');
    case 'loyalty_rules':
    case 'birthday_rewards':
      return list('/loyalty', 'Crave Credit');
    case 'integration_settings':
    case 'integration_secrets':
      return list('/settings/integrations', 'Integrations');
    default:
      return null;
  }
}

// ------------------------------------------------------------------ field table

const FIELD_NAMES: Record<string, string> = {
  id: 'Record id',
  is_active: 'Active',
  display_name: 'Name',
  auth_user_id: 'Sign-in account',
  mfa_required: 'Two-factor required',
  last_login_at: 'Last sign-in',
  created_at: 'Created',
  updated_at: 'Last updated',
  base_unit: 'Base unit',
  display_unit: 'Show and count in',
  density_g_per_ml: 'Density (grams per ml)',
  each_weight_g: 'Weight of one (grams)',
  on_hand_delta: 'Change on hand',
  reserved_delta: 'Change reserved',
  tax_rate_bps: 'Tax rate',
  resume_status: 'Stage to go back to',
  aal: 'Two-factor level',
};

function fieldLabel(key: string): string {
  const known = FIELD_NAMES[key];
  if (known) return known;
  const k = key
    .replace(/_cents$/, '')
    .replace(/_bps$/, '')
    .replace(/_at$/, '')
    .replace(/^is_/, '');
  return humanize(k);
}

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

function valueText(key: string, value: unknown): { text: string; mono?: boolean; muted?: boolean } {
  if (value === undefined) return { text: 'Not there', muted: true };
  if (value === null || value === '') return { text: 'Empty', muted: true };
  if (typeof value === 'boolean') return { text: value ? 'Yes' : 'No' };
  if (typeof value === 'number') {
    if (key.endsWith('_cents')) return { text: money(value) };
    if (key.endsWith('_bps')) return { text: `${(value / 100).toLocaleString('en-US')}%` };
    return { text: value.toLocaleString('en-US', { maximumFractionDigits: 4 }) };
  }
  if (typeof value === 'string') {
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value)) return { text: formatInstant(value, true) };
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return { text: formatDate(value) };
    if (/^[A-Z][A-Z0-9]*(_[A-Z0-9]+)*$/.test(value) && value.length >= 3 && !KEEP_CAPS.has(value))
      return { text: capitalize(value.replace(/_/g, ' ').toLowerCase()) };
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value))
      return { text: value, mono: true };
    return { text: value };
  }
  if (Array.isArray(value) && value.every((v) => typeof v === 'string'))
    return { text: value.length ? value.join(', ') : 'None' };
  return { text: JSON.stringify(value), mono: true };
}

interface FieldRow {
  key: string;
  before: unknown;
  after: unknown;
  changed: boolean;
}

function fieldRows(item: AuditItem): {
  rows: FieldRow[];
  mode: 'update' | 'create' | 'delete' | 'none';
} {
  const before = isRecord(item.before) ? item.before : null;
  const after = isRecord(item.after) ? item.after : null;
  const changes = isRecord(item.changed_fields)
    ? (item.changed_fields as Record<string, { from?: unknown; to?: unknown }>)
    : null;
  const keys: string[] = [];
  const add = (k: string) => {
    if (!keys.includes(k)) keys.push(k);
  };
  Object.keys(after ?? {}).forEach(add);
  Object.keys(before ?? {}).forEach(add);
  Object.keys(changes ?? {}).forEach(add);
  const mode =
    before && after ? 'update' : after ? 'create' : before ? 'delete' : changes ? 'update' : 'none';
  const rows = keys.map((key) => {
    const change = changes?.[key];
    const b = change && 'from' in change ? change.from : before ? before[key] : undefined;
    const a = change && 'to' in change ? change.to : after ? after[key] : undefined;
    const changed =
      mode === 'update'
        ? changes
          ? key in changes
          : JSON.stringify(before?.[key]) !== JSON.stringify(after?.[key])
        : false;
    return { key, before: b, after: a, changed };
  });
  return { rows, mode };
}

function ValueCell({
  k,
  value,
  fallback,
}: {
  k: string;
  value: unknown;
  fallback?: string | undefined;
}) {
  if (value === undefined && fallback) return <span className="crave-audit-muted">{fallback}</span>;
  const v = valueText(k, value);
  return (
    <span
      className={[v.mono ? 'crave-audit-mono' : '', v.muted ? 'crave-audit-muted' : ''].join(' ')}
    >
      {v.text}
    </span>
  );
}

function ChangesTable({ item }: { item: AuditItem }) {
  const { rows, mode } = fieldRows(item);
  const [showAll, setShowAll] = useState(false);
  if (mode === 'none' || rows.length === 0) return null;
  const changedRows = rows.filter((r) => r.changed);
  const shown = mode === 'update' && !showAll && changedRows.length > 0 ? changedRows : rows;
  const title =
    mode === 'create' ? 'What was saved' : mode === 'delete' ? 'What was deleted' : 'What changed';
  return (
    <div className="crave-audit-block">
      <h4 className="crave-audit-h">{title}</h4>
      <div className="crave-audit-scroll">
        <table className="crave-audit-table">
          <thead>
            <tr>
              <th scope="col">Field</th>
              <th scope="col">Before</th>
              <th scope="col">After</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <tr key={r.key} data-changed={r.changed ? 'true' : undefined}>
                <th scope="row">
                  {fieldLabel(r.key)}
                  {r.changed && <span className="crave-audit-sr"> (changed)</span>}
                </th>
                <td>
                  <ValueCell
                    k={r.key}
                    value={r.before}
                    fallback={mode === 'create' ? 'Not there yet' : undefined}
                  />
                </td>
                <td>
                  <ValueCell
                    k={r.key}
                    value={r.after}
                    fallback={mode === 'delete' ? 'Deleted' : undefined}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {mode === 'update' && changedRows.length > 0 && rows.length > changedRows.length && (
        <div>
          <Button
            variant="ghost"
            onClick={() => setShowAll((s) => !s)}
            style={{ padding: '0 4px' }}
          >
            {showAll ? 'Show only what changed' : `Show all ${rows.length} fields`}
          </Button>
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ detail panel

function Facts({ items }: { items: [string, ReactNode][] }) {
  return (
    <dl className="crave-audit-facts">
      {items.map(([label, value]) => (
        <Fragment key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </Fragment>
      ))}
    </dl>
  );
}

function longWhen(item: AuditItem): string {
  const d = new Date(item.created_at);
  if (Number.isNaN(d.getTime())) return item.local_time;
  return `${new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    timeZone: LOCATION_TIMEZONE,
  }).format(d)} (Florida time)`;
}

function twoFactorText(aal: string | null | undefined): string {
  if (aal === 'aal2') return 'Yes, verified with a second step';
  if (aal === 'aal1') return 'No, password or email code only';
  return 'Not recorded';
}

function EntryDetail({
  item: listed,
  onShowRecord,
  id,
}: {
  item: AuditItem;
  onShowRecord: (type: string, entityId: string) => void;
  id: string;
}) {
  const q = useQuery({
    queryKey: ['audit-entry', listed.id],
    queryFn: async () => (await api.get<AuditDetail>(`/admin/audit/${listed.id}`)).data,
    staleTime: Infinity,
  });
  const [allRelated, setAllRelated] = useState(false);
  const item = q.data?.item ?? listed;
  const request = q.data?.request ?? null;
  const related = q.data?.related ?? [];
  const actor = item.actor;
  const status = request?.status ?? item.request?.status ?? null;
  const code = request?.error_code ?? item.request?.error_code ?? null;
  const duration = request?.duration_ms ?? item.request?.duration_ms ?? null;
  const method = request?.method ?? item.request?.method ?? null;
  const path = request?.path ?? item.request?.path ?? null;
  const raw = {
    before: item.before ?? null,
    after: item.after ?? null,
    changed_fields: item.changed_fields ?? null,
    request: request ? { query: request.query, body: request.body } : null,
  };
  const person: ReactNode =
    actor.type === 'STAFF' ? (
      <>
        {actor.name ?? 'A staff member'}
        {actor.email && <span className="crave-audit-muted"> ({actor.email})</span>}
        {actor.roles.length > 0 && <div>{roleLabel(actor.roles)} at the time</div>}
      </>
    ) : (
      personLabel(actor)
    );
  const entity = item.entity;
  const relatedShown = allRelated ? related : related.slice(0, 8);

  return (
    <div className="crave-audit-detail" id={id}>
      {item.reason && (
        <div className="crave-audit-block">
          <h4 className="crave-audit-h">Reason or note</h4>
          <blockquote className="crave-audit-reason">{item.reason}</blockquote>
        </div>
      )}
      <ChangesTable item={item} />
      <div className="crave-audit-block">
        <h4 className="crave-audit-h">Who, when and where</h4>
        <Facts
          items={[
            ['When', longWhen(item)],
            ['Who', person],
            ...(entity
              ? ([
                  [
                    'Record',
                    <>
                      {capitalize(entity.noun ?? nounFor(entity.type))}
                      {recordName(item) ? ` “${recordName(item)}”` : ''}
                      {entity.id && (
                        <div>
                          <Button
                            variant="ghost"
                            style={{ padding: '0 4px', minHeight: 36 }}
                            onClick={() => onShowRecord(entity.type, entity.id ?? '')}
                          >
                            Every change to this record
                          </Button>
                        </div>
                      )}
                    </>,
                  ],
                ] as [string, ReactNode][])
              : []),
            ['What happened', actionLabel(item.action)],
            ['App', item.app ? (APP_NAMES[item.app] ?? humanize(item.app)) : 'Not recorded'],
            ['Two-factor sign-in', twoFactorText(item.aal ?? request?.aal)],
            ['Device and browser', describeDevice(item.user_agent ?? request?.user_agent)],
            ['IP address', item.ip ?? request?.ip ?? 'Not recorded'],
            ['Result', resultText(status, code)],
            ['Time it took', durationText(duration)],
            [
              'Request id',
              item.request_id ? (
                <span className="crave-audit-mono">{item.request_id}</span>
              ) : (
                'Not recorded'
              ),
            ],
            ...(method && path
              ? ([
                  [
                    'Server address',
                    <span key="p" className="crave-audit-mono">
                      {method} {path}
                    </span>,
                  ],
                ] as [string, ReactNode][])
              : []),
          ]}
        />
        {q.isLoading && item.request_id && (
          <p className="crave-audit-muted" style={{ margin: 0, fontSize: 14 }}>
            Loading the request details…
          </p>
        )}
        {q.isError && (
          <Notice kind="error">{errorText(q.error, 'Could not load the request details.')}</Notice>
        )}
      </div>
      {related.length > 0 && (
        <div className="crave-audit-block">
          <h4 className="crave-audit-h">Also changed at the same time ({related.length})</h4>
          <ul className="crave-audit-related">
            {relatedShown.map((r) => (
              <li key={r.id}>
                {r.summary
                  ? tidyText(r.summary)
                  : `${actionLabel(r.action)}: ${nounFor(r.entity_type)}${r.entity_label ? ` “${r.entity_label}”` : ''}.`}
              </li>
            ))}
          </ul>
          {related.length > relatedShown.length && (
            <div>
              <Button
                variant="ghost"
                style={{ padding: '0 4px' }}
                onClick={() => setAllRelated(true)}
              >
                Show all {related.length}
              </Button>
            </div>
          )}
        </div>
      )}
      <details className="crave-audit-raw">
        <summary>Raw data (for troubleshooting)</summary>
        <pre>{JSON.stringify(raw, null, 2)}</pre>
      </details>
    </div>
  );
}

// ------------------------------------------------------------------ timeline

function Badge({ tone, children }: { tone?: 'ok' | 'red'; children: ReactNode }) {
  return (
    <span className="crave-audit-badge" data-tone={tone}>
      {children}
    </span>
  );
}

function EntryRow({
  item,
  sub = false,
  open,
  onToggle,
  onShowRecord,
}: {
  item: AuditItem;
  /** Made at the same time as the entry above it. */
  sub?: boolean;
  open: boolean;
  onToggle: () => void;
  onShowRecord: (type: string, entityId: string) => void;
}) {
  const detailId = useId();
  const link = recordLink(item);
  const refused = item.kind === 'request';
  const failed = refused && (item.request?.status ?? 0) >= 500;
  return (
    <li
      className="crave-audit-entry"
      data-open={open ? 'true' : undefined}
      data-sub={sub ? 'true' : undefined}
    >
      <div className="crave-audit-row">
        <div className="crave-audit-time">{clockTime(item.local_time)}</div>
        <div className="crave-audit-main">
          <p className="crave-audit-summary">{plainSummary(item)}</p>
          <div className="crave-audit-meta">
            <span>{personLabel(item.actor)}</span>
            {refused && <Badge tone="red">{failed ? 'Failed' : 'Refused'}</Badge>}
            {item.app && <Badge>{APP_BADGES[item.app] ?? humanize(item.app)}</Badge>}
            {item.aal === 'aal2' && <Badge tone="ok">Two-factor</Badge>}
            {link && (
              <Link to={link.to} className="crave-audit-link crave-hit">
                {link.text}
              </Link>
            )}
          </div>
        </div>
        <Button
          variant="secondary"
          className="crave-audit-toggle"
          aria-expanded={open}
          aria-controls={open ? detailId : undefined}
          onClick={onToggle}
          style={{ padding: '0 14px' }}
        >
          {open ? 'Hide' : 'Details'}
        </Button>
      </div>
      {open && <EntryDetail item={item} id={detailId} onShowRecord={onShowRecord} />}
    </li>
  );
}

/** Entries that repeat one another (the same person, record, action and fields). */
function similarKey(item: AuditItem): string {
  return [
    item.kind,
    item.actor.type,
    item.actor.id,
    item.action,
    item.entity?.type,
    item.entity?.id,
    Object.keys(item.changed_fields ?? {})
      .sort()
      .join(','),
    item.request?.route,
    item.request?.status,
  ].join('|');
}

/** Records that change because of another change (a lot drawn down when an order is made). */
const SIDE_EFFECT_TYPES = new Set([
  'inventory_lots',
  'inventory_stock',
  'pickup_slots',
  'recipe_version_ingredients',
  'receipt_items',
  'physical_count_lines',
  'menu_item_modifiers',
  'ingredient_packages',
  'user_roles',
  'payments',
  'loyalty_accounts',
]);

function weight(item: AuditItem): number {
  if (item.kind === 'request' || item.action.includes('.')) return 3;
  return item.entity && SIDE_EFFECT_TYPES.has(item.entity.type) ? 1 : 2;
}

/** The entry that best says what one step did: a named action, else the main record; the earliest on ties. */
export function primaryOf(items: readonly AuditItem[]): AuditItem {
  let best = items[0]!;
  for (const item of items) if (weight(item) >= weight(best)) best = item;
  return best;
}

/** Neighbouring entries made by one request (one press of a button), in list order. */
export function groupSteps(items: readonly AuditItem[]): AuditItem[][] {
  const steps: AuditItem[][] = [];
  for (const item of items) {
    const last = steps.at(-1);
    const head = last?.[0];
    if (
      last &&
      head &&
      item.kind === 'change' &&
      head.kind === 'change' &&
      item.request_id &&
      head.request_id === item.request_id
    )
      last.push(item);
    else steps.push([item]);
  }
  return steps;
}

interface Run {
  /** 'step': one request's changes, main entry first. 'similar': separate entries that repeat. */
  kind: 'step' | 'similar';
  primary: AuditItem;
  items: AuditItem[];
}

function groupByDay(items: AuditItem[]): { date: string; runs: Run[] }[] {
  const days: { date: string; runs: Run[] }[] = [];
  let lastKey = '';
  for (const step of groupSteps(items)) {
    const first = step[0]!;
    const date = first.local_time.slice(0, 10);
    let day = days.at(-1);
    if (!day || day.date !== date) {
      day = { date, runs: [] };
      days.push(day);
      lastKey = '';
    }
    if (step.length > 1) {
      const primary = primaryOf(step);
      day.runs.push({
        kind: 'step',
        primary,
        items: [primary, ...step.filter((i) => i !== primary)],
      });
      lastKey = '';
      continue;
    }
    const key = similarKey(first);
    const run = day.runs.at(-1);
    if (run && run.kind === 'similar' && key === lastKey) run.items.push(first);
    else day.runs.push({ kind: 'similar', primary: first, items: [first] });
    lastKey = key;
  }
  return days;
}

// ------------------------------------------------------------------ filters and page

type Range = 'today' | '7d' | '30d' | 'all' | 'custom';
const RANGE_OPTIONS: readonly (readonly [Range, string])[] = [
  ['today', 'Today'],
  ['7d', 'Last 7 days'],
  ['30d', 'Last 30 days'],
  ['all', 'All time'],
  ['custom', 'Pick dates'],
];

const FILTER_KEYS = [
  'actor_id',
  'actor_type',
  'entity_type',
  'entity_id',
  'action',
  'app',
  'q',
  'refused',
] as const;

function rangeDates(range: Range, from: string, to: string, todayDate: string) {
  switch (range) {
    case 'today':
      return { from: todayDate, to: todayDate };
    case '7d':
      return { from: addDays(todayDate, -6), to: todayDate };
    case '30d':
      return { from: addDays(todayDate, -29), to: todayDate };
    case 'custom':
      return { from, to };
    default:
      return { from: '', to: '' };
  }
}

async function downloadCsv(query: string): Promise<string> {
  const url = `${apiBaseUrl}/v1/admin/audit/export.csv${query ? `?${query}` : ''}`;
  const token = await getAccessToken();
  const get = (t: string | null) =>
    fetch(url, { headers: { Accept: 'text/csv', ...(t ? { Authorization: `Bearer ${t}` } : {}) } });
  let res = await get(token);
  if (res.status === 401 && token) {
    const fresh = await refreshAccessToken().catch(() => null);
    if (fresh && fresh !== token) res = await get(fresh);
  }
  if (!res.ok) {
    let message = `The download did not work (the server answered ${res.status}).`;
    try {
      const body = (await res.json()) as { error?: { message?: string } };
      if (body.error?.message) message = body.error.message;
    } catch {
      /* keep the generic message */
    }
    throw new Error(message);
  }
  const blob = await res.blob();
  const name =
    /filename="?([^";]+)"?/.exec(res.headers.get('Content-Disposition') ?? '')?.[1] ??
    `crave-audit-${today()}.csv`;
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = name;
  a.rel = 'noopener';
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 30_000);
  return name;
}

const CSS = `
.crave-audit-intro { margin: 0; color: var(--ink-soft); max-width: 70ch; }
.crave-audit-tools { display: flex; flex-wrap: wrap; gap: 8px; }
.crave-audit-check { display: inline-flex; align-items: center; gap: 10px; min-height: var(--touch); font-weight: 600; cursor: pointer; }
.crave-audit-check input { width: 20px; height: 20px; margin: 0; accent-color: var(--crave-red); flex: none; }
.crave-audit-note { margin: 12px 0 0; font-size: 14px; color: var(--ink-soft); }
.crave-audit-scope { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 8px 16px; padding: 12px 16px; border: 2px solid var(--ink); border-radius: var(--radius-sm); background: var(--paper); }
.crave-audit-scope p { margin: 0; min-width: 0; overflow-wrap: anywhere; }
.crave-audit-day + .crave-audit-day { margin-top: 20px; }
.crave-audit-dayhead { margin: 0 0 4px; font-size: 15px; font-weight: 700; }
.crave-audit-list { list-style: none; margin: 0; padding: 0; }
.crave-audit-entry { container: crave-audit-entry / inline-size; border-top: 1px solid var(--line); scroll-margin-top: 72px; }
[id^="entry-"] { scroll-margin-top: 72px; }
.crave-audit-row { display: grid; grid-template-columns: 5.5rem minmax(0, 1fr) auto; gap: 4px 12px; padding: 12px 0; align-items: start; }
.crave-audit-time { font-variant-numeric: tabular-nums; color: var(--ink-soft); font-size: 14px; padding-top: 2px; white-space: nowrap; }
.crave-audit-summary { margin: 0; line-height: 1.4; overflow-wrap: anywhere; }
.crave-audit-meta { display: flex; flex-wrap: wrap; gap: 4px 10px; align-items: center; margin-top: 6px; font-size: 14px; color: var(--ink-soft); min-width: 0; overflow-wrap: anywhere; }
.crave-audit-link { color: var(--ink); font-weight: 600; text-decoration: underline; text-underline-offset: 3px; }
.crave-audit-badge { display: inline-flex; align-items: center; padding: 0 8px; border-radius: 999px; font-size: 12px; font-weight: 700; line-height: 20px; border: 1px solid var(--line); background: #f6f3ee; color: var(--ink); white-space: nowrap; }
.crave-audit-badge[data-tone='ok'] { border-color: var(--ok); color: var(--ok); background: #eef6f1; }
.crave-audit-badge[data-tone='red'] { border-color: var(--crave-red); color: var(--crave-red); background: #fdeeee; }
.crave-audit-more { border-top: 1px dashed var(--line); padding: 4px 0 8px 6.25rem; }
.crave-audit-entry[data-sub] { border-top-style: dashed; }
.crave-audit-entry[data-sub] .crave-audit-main { border-left: 3px solid var(--line); padding-left: 10px; }
@container crave-audit-entry (max-width: 520px) {
  .crave-audit-row { grid-template-columns: minmax(0, 1fr) auto; }
  .crave-audit-time { grid-column: 1; grid-row: 1; align-self: center; }
  .crave-audit-toggle { grid-column: 2; grid-row: 1; }
  .crave-audit-main { grid-column: 1 / -1; grid-row: 2; }
}
@media (max-width: 760px) { .crave-audit-more { padding-left: 0; } }
.crave-audit-detail { container: crave-audit-detail / inline-size; display: grid; gap: 20px; margin: 0 0 16px; padding: 16px; border: 1px solid var(--line); border-radius: var(--radius-sm); background: #faf8f4; min-width: 0; }
.crave-audit-block { display: grid; gap: 8px; min-width: 0; }
.crave-audit-h { margin: 0; font-size: 15px; }
.crave-audit-reason { margin: 0; padding: 8px 12px; border-left: 4px solid var(--ink); background: var(--paper); white-space: pre-wrap; overflow-wrap: anywhere; }
.crave-audit-scroll { overflow-x: auto; background: var(--paper); border: 1px solid var(--line); border-radius: var(--radius-sm); }
.crave-audit-table { width: 100%; border-collapse: collapse; font-size: 14px; }
.crave-audit-table th, .crave-audit-table td { text-align: left; vertical-align: top; padding: 8px 10px; border-bottom: 1px solid var(--line); overflow-wrap: anywhere; min-width: 5.5rem; }
.crave-audit-table thead th { color: var(--ink-soft); font-weight: 600; border-bottom-width: 2px; white-space: nowrap; }
.crave-audit-table tbody th { font-weight: 600; width: 30%; }
.crave-audit-table tbody tr:last-child > * { border-bottom: 0; }
.crave-audit-table tr[data-changed] > * { background: #fff4d6; }
.crave-audit-table tr[data-changed] > th { box-shadow: inset 4px 0 0 var(--warn); }
.crave-audit-table tr[data-changed] > td:last-child { font-weight: 700; }
.crave-audit-facts { display: grid; grid-template-columns: minmax(8rem, max-content) minmax(0, 1fr); gap: 6px 16px; margin: 0; font-size: 14px; }
.crave-audit-facts dt { color: var(--ink-soft); font-weight: 600; }
.crave-audit-facts dd { margin: 0; min-width: 0; overflow-wrap: anywhere; }
@container crave-audit-detail (max-width: 460px) {
  .crave-audit-facts { grid-template-columns: minmax(0, 1fr); gap: 0; }
  .crave-audit-facts dd { margin-bottom: 8px; }
}
.crave-audit-related { margin: 0; padding-left: 18px; font-size: 14px; display: grid; gap: 4px; overflow-wrap: anywhere; }
.crave-audit-raw summary { cursor: pointer; font-weight: 600; min-height: 32px; display: flex; align-items: center; }
.crave-audit-raw pre { margin: 8px 0 0; padding: 12px; background: var(--paper); border: 1px solid var(--line); border-radius: var(--radius-sm); font-size: 12px; line-height: 1.45; max-height: 360px; overflow: auto; white-space: pre-wrap; overflow-wrap: anywhere; }
.crave-audit-mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 13px; overflow-wrap: anywhere; }
.crave-audit-muted { color: var(--ink-soft); }
.crave-audit-sr { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }
.crave-audit-end { margin: 16px 0 0; text-align: center; color: var(--ink-soft); font-size: 14px; }
`;

export function AuditTrail() {
  const { can } = useStaff();
  const [params, setParams] = useSearchParams();
  const todayDate = today();

  const entityType = params.get('entity_type') ?? '';
  const entityId = params.get('entity_id') ?? '';
  const rawRange = params.get('range') as Range | null;
  const range: Range =
    rawRange && RANGE_OPTIONS.some(([v]) => v === rawRange) ? rawRange : entityId ? 'all' : '7d';
  const customFrom = params.get('from') ?? '';
  const customTo = params.get('to') ?? '';
  const actorId = params.get('actor_id') ?? '';
  const actorType = params.get('actor_type') ?? '';
  const action = params.get('action') ?? '';
  const app = params.get('app') ?? '';
  const q = params.get('q') ?? '';
  const refused = params.get('refused') === '1';
  const selected = params.get('entry') ?? '';

  const [search, setSearch] = useState(q);
  const [expandedRuns, setExpandedRuns] = useState<ReadonlySet<string>>(new Set());
  const [exporting, setExporting] = useState(false);
  const [exportMsg, setExportMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  const update = (changes: Record<string, string | null>) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(changes)) {
          if (v === null || v === '') next.delete(k);
          else next.set(k, v);
        }
        if (!('entry' in changes)) next.delete('entry');
        return next;
      },
      { replace: true },
    );

  // The search box writes to the address a moment after typing stops.
  useEffect(() => {
    const t = setTimeout(() => {
      if (search.trim() !== q) update({ q: search.trim() || null });
    }, 400);
    return () => clearTimeout(t);
  }, [search]);

  // Opened from a link (a Dashboard change): bring that entry into view once it has loaded,
  // so a phone doesn't land on the filters with nothing visibly opened.
  const arrivedWith = useRef(selected);
  useEffect(() => {
    const id = arrivedWith.current;
    if (!id) return;
    let tries = 0;
    const timer = window.setInterval(() => {
      const el =
        document.getElementById(`entry-${id}`) ??
        document.querySelector('.crave-audit-entry[data-open="true"]');
      if (el || ++tries > 40) {
        window.clearInterval(timer);
        arrivedWith.current = '';
        el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 150);
    return () => window.clearInterval(timer);
  }, []);
  // Back and forward buttons change the address; keep the box in step.
  useEffect(() => {
    setSearch((s) => (s.trim() === q ? s : q));
  }, [q]);

  const dates = rangeDates(range, customFrom, customTo, todayDate);
  const badRange = range === 'custom' && !!dates.from && !!dates.to && dates.from > dates.to;

  const apiQuery = useMemo(() => {
    const p = new URLSearchParams();
    if (dates.from) p.set('from', dates.from);
    if (dates.to) p.set('to', dates.to);
    if (actorId) p.set('actor_id', actorId);
    else if (actorType) p.set('actor_type', actorType);
    if (entityType) p.set('entity_type', entityType);
    if (entityId) p.set('entity_id', entityId);
    if (action) p.set('action', action);
    if (app) p.set('app', app);
    if (q) p.set('q', q);
    if (refused) p.set('include_requests', 'true');
    return p.toString();
  }, [dates.from, dates.to, actorId, actorType, entityType, entityId, action, app, q, refused]);

  const allowed = can('audit.read');
  const facetQuery = new URLSearchParams();
  if (dates.from) facetQuery.set('from', dates.from);
  if (dates.to) facetQuery.set('to', dates.to);
  const facets = useQuery({
    queryKey: ['audit-facets', dates.from, dates.to],
    queryFn: async () =>
      (await api.get<Facets>(`/admin/audit/facets?${facetQuery.toString()}`)).data,
    enabled: allowed && !badRange,
    staleTime: 60_000,
  });

  const list = useInfiniteQuery({
    queryKey: ['audit', apiQuery],
    queryFn: async ({ pageParam }) =>
      (
        await api.get<AuditPage>(
          `/admin/audit?${apiQuery}${pageParam ? `${apiQuery ? '&' : ''}cursor=${encodeURIComponent(pageParam)}` : ''}`,
        )
      ).data,
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    enabled: allowed && !badRange,
  });

  // A delete with a named entry (ingredient.delete, with what went with it) also leaves the plain
  // row delete from the same request. Show the named one; the other stays under "Also changed".
  const items = useMemo(() => {
    const all = list.data?.pages.flatMap((p) => p.items) ?? [];
    const recordKey = (i: AuditItem) => `${i.request_id}|${i.entity?.type}|${i.entity?.id}`;
    const named = new Set(
      all
        .filter((i) => i.kind === 'change' && i.request_id && /\.delete$/.test(i.action))
        .map(recordKey),
    );
    return all.filter(
      (i) =>
        !(
          i.kind === 'change' &&
          i.action === 'delete' &&
          i.request_id &&
          i.id !== selected &&
          named.has(recordKey(i))
        ),
    );
  }, [list.data, selected]);
  const days = useMemo(() => groupByDay(items), [items]);
  const selectedInList = !!selected && items.some((i) => i.id === selected);

  if (!allowed) {
    return (
      <Section title="Audit trail">
        <Notice>
          Your role does not include the audit trail. The owner can give access in Staff.
        </Notice>
      </Section>
    );
  }

  const f = facets.data;
  const personOptions: [string, string][] = [
    ...(f?.actors ?? []).map(
      (a) =>
        [
          `staff:${a.id}`,
          `${a.name ?? a.email ?? 'Staff member'}${a.on_staff ? '' : ' (no longer on staff)'}`,
        ] as [string, string],
    ),
    ...(f?.actor_types ?? [])
      .filter((t) => t.type !== 'STAFF')
      .map(
        (t) =>
          [`type:${t.type}`, ACTOR_TYPE_NAMES[t.type] ?? humanize(t.type.toLowerCase())] as [
            string,
            string,
          ],
      ),
  ];
  const personValue = actorId ? `staff:${actorId}` : actorType ? `type:${actorType}` : '';
  if (personValue && !personOptions.some(([v]) => v === personValue)) {
    personOptions.push([
      personValue,
      actorType
        ? (ACTOR_TYPE_NAMES[actorType] ?? humanize(actorType.toLowerCase()))
        : 'The person picked',
    ]);
  }
  if (!personOptions.some(([v]) => v === 'type:SYSTEM'))
    personOptions.push(['type:SYSTEM', ACTOR_TYPE_NAMES['SYSTEM']!]);
  if (!personOptions.some(([v]) => v === 'type:CUSTOMER'))
    personOptions.push(['type:CUSTOMER', ACTOR_TYPE_NAMES['CUSTOMER']!]);

  const typeOptions: [string, string][] = (f?.entity_types ?? []).map((t) => [
    t.type,
    capitalize(t.label ?? nounFor(t.type)),
  ]);
  if (entityType && !typeOptions.some(([v]) => v === entityType))
    typeOptions.push([entityType, capitalize(nounFor(entityType))]);
  typeOptions.sort((a, b) => a[1].localeCompare(b[1]));

  const actionOptions: [string, string][] = (f?.actions ?? []).map((a) => [
    a.action,
    actionLabel(a.action),
  ]);
  if (action && !actionOptions.some(([v]) => v === action))
    actionOptions.push([action, actionLabel(action)]);
  actionOptions.sort((a, b) => a[1].localeCompare(b[1]));

  const appOptions = Object.entries(APP_NAMES);

  const scoped = items.find((i) => i.entity?.id === entityId && i.entity.type === entityType);
  const scopeNoun =
    scoped?.entity?.noun ??
    f?.entity_types.find((t) => t.type === entityType)?.label ??
    nounFor(entityType);
  const scopeLabel = scoped ? recordName(scoped) : null;

  const anyFilter = FILTER_KEYS.some((k) => params.has(k)) || params.has('range');
  const refusedIgnored = refused && (!!action || (!!entityType && !entityId));

  const showRecord = (type: string, id: string) =>
    update({
      entity_type: type,
      entity_id: id,
      range: 'all',
      actor_id: null,
      actor_type: null,
      action: null,
      app: null,
      q: null,
    });

  const toggle = (id: string) => update({ entry: selected === id ? null : id });

  const exportCsv = async () => {
    setExporting(true);
    setExportMsg(null);
    try {
      const name = await downloadCsv(apiQuery);
      setExportMsg({ kind: 'ok', text: `Downloaded ${name}. It opens in Numbers or Excel.` });
    } catch (e) {
      setExportMsg({
        kind: 'error',
        text: e instanceof Error && e.message ? e.message : 'The download did not work. Try again.',
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <Section
      title="Audit trail"
      right={
        <div className="crave-audit-tools">
          {anyFilter && (
            <Button
              variant="ghost"
              onClick={() => {
                setSearch('');
                setParams(new URLSearchParams(), { replace: true });
              }}
            >
              Clear filters
            </Button>
          )}
          <Button
            variant="secondary"
            onClick={() => void exportCsv()}
            disabled={exporting || badRange}
          >
            {exporting ? 'Preparing file…' : 'Export CSV'}
          </Button>
        </div>
      }
    >
      <style>{CSS}</style>
      <p className="crave-audit-intro">
        Every change made in Crave: who made it, when, from where and exactly what changed. Entries
        can’t be edited or deleted. Times are Florida time.
      </p>

      <Card style={{ padding: 16 }}>
        <FormGrid min={200}>
          <SelectField
            label="When"
            options={RANGE_OPTIONS}
            value={range}
            onChange={(e) => {
              const v = e.target.value as Range;
              if (v === 'custom')
                update({
                  range: v,
                  from: customFrom || dates.from || addDays(todayDate, -6),
                  to: customTo || dates.to || todayDate,
                });
              else update({ range: v, from: null, to: null });
            }}
          />
          {range === 'custom' && (
            <Field
              label="From"
              type="date"
              value={customFrom}
              max={todayDate}
              onChange={(e) => update({ from: e.target.value || null })}
            />
          )}
          {range === 'custom' && (
            <Field
              label="To"
              type="date"
              value={customTo}
              max={todayDate}
              error={badRange ? 'Pick a day on or after From.' : undefined}
              onChange={(e) => update({ to: e.target.value || null })}
            />
          )}
          <SelectField
            label="Person"
            placeholder="Everyone"
            options={personOptions}
            value={personValue}
            onChange={(e) => {
              const v = e.target.value;
              update({
                actor_id: v.startsWith('staff:') ? v.slice(6) : null,
                actor_type: v.startsWith('type:') ? v.slice(5) : null,
              });
            }}
          />
          <SelectField
            label="Record type"
            placeholder="All records"
            options={typeOptions}
            value={entityType}
            onChange={(e) => update({ entity_type: e.target.value || null, entity_id: null })}
          />
          <SelectField
            label="What happened"
            placeholder="Anything"
            options={actionOptions}
            value={action}
            onChange={(e) => update({ action: e.target.value || null })}
          />
          <SelectField
            label="App"
            placeholder="All apps"
            options={appOptions}
            value={app}
            onChange={(e) => update({ app: e.target.value || null })}
          />
          <Field
            label="Search"
            type="search"
            placeholder="Name, summary or reason"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <FormAction>
            <label className="crave-audit-check">
              <input
                type="checkbox"
                checked={refused}
                onChange={(e) => update({ refused: e.target.checked ? '1' : null })}
              />
              Show refused attempts
              {f && f.refused_requests > 0 ? ` (${f.refused_requests})` : ''}
            </label>
          </FormAction>
        </FormGrid>
        {refusedIgnored && (
          <p className="crave-audit-note">
            Refused attempts are left out while “What happened” or a record type is picked.
          </p>
        )}
      </Card>

      {exportMsg && <Notice kind={exportMsg.kind}>{exportMsg.text}</Notice>}

      {entityId && (
        <div className="crave-audit-scope">
          <p>
            Showing every change to one {scopeNoun}
            {scopeLabel ? (
              <>
                : <strong>{scopeLabel}</strong>
              </>
            ) : null}
            .
          </p>
          <Button
            variant="secondary"
            onClick={() => update({ entity_id: null, entity_type: null })}
          >
            Show all records
          </Button>
        </div>
      )}

      {selected && !selectedInList && !list.isLoading && (
        <SelectedEntry
          id={selected}
          onClose={() => update({ entry: null })}
          onShowRecord={showRecord}
        />
      )}

      <Card style={{ padding: '16px 20px' }}>
        {list.isError ? (
          <Notice kind="error">{errorText(list.error, 'Could not load the audit trail.')}</Notice>
        ) : badRange ? (
          <p className="crave-audit-muted" style={{ margin: 0 }}>
            Fix the dates above to see the trail.
          </p>
        ) : list.isLoading ? (
          <p className="crave-audit-muted" style={{ margin: 0 }}>
            Loading…
          </p>
        ) : items.length === 0 ? (
          <div style={{ display: 'grid', gap: 8, justifyItems: 'start' }}>
            <p style={{ margin: 0 }}>Nothing matches these filters.</p>
            <p className="crave-audit-muted" style={{ margin: 0, fontSize: 14 }}>
              {range === 'all'
                ? 'Try clearing a filter or the search.'
                : 'Try a longer time span, such as All time, or clear a filter.'}
            </p>
          </div>
        ) : (
          <>
            {days.map((day) => (
              <section
                key={day.date}
                className="crave-audit-day"
                aria-label={dayHeading(day.date, todayDate)}
              >
                <h3 className="crave-audit-dayhead">{dayHeading(day.date, todayDate)}</h3>
                <ul className="crave-audit-list">
                  {day.runs.map((run) => {
                    const first = run.primary;
                    const step = run.kind === 'step';
                    const others = run.items.length - 1;
                    const collapsible = step ? others >= 1 : run.items.length >= 3;
                    const expanded =
                      !collapsible ||
                      expandedRuns.has(first.id) ||
                      run.items.some((i) => i !== first && i.id === selected);
                    const shown = expanded ? run.items : [first];
                    const moreText = step
                      ? expanded
                        ? 'Hide the other changes made at the same time'
                        : `Show ${others} more ${others === 1 ? 'change' : 'changes'} made at the same time`
                      : expanded
                        ? 'Show fewer like this'
                        : `Show ${others} more like this`;
                    return (
                      <Fragment key={first.id}>
                        {shown.map((item) => (
                          <EntryRow
                            key={item.id}
                            item={item}
                            sub={step && item !== first}
                            open={selected === item.id}
                            onToggle={() => toggle(item.id)}
                            onShowRecord={showRecord}
                          />
                        ))}
                        {collapsible && (
                          <li className="crave-audit-more">
                            <Button
                              variant="ghost"
                              style={{ padding: '0 6px' }}
                              aria-expanded={expanded}
                              onClick={() =>
                                setExpandedRuns((prev) => {
                                  const next = new Set(prev);
                                  if (next.has(first.id)) next.delete(first.id);
                                  else next.add(first.id);
                                  return next;
                                })
                              }
                            >
                              {moreText}
                            </Button>
                          </li>
                        )}
                      </Fragment>
                    );
                  })}
                </ul>
              </section>
            ))}
            {list.hasNextPage ? (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
                <Button
                  variant="secondary"
                  onClick={() => void list.fetchNextPage()}
                  disabled={list.isFetchingNextPage}
                >
                  {list.isFetchingNextPage ? 'Loading…' : 'Load more'}
                </Button>
              </div>
            ) : (
              <p className="crave-audit-end">That is everything for these filters.</p>
            )}
            {list.isFetchNextPageError && (
              <div style={{ marginTop: 12 }}>
                <Notice kind="error">
                  Could not load more entries. Check the connection and press Load more again.
                </Notice>
              </div>
            )}
          </>
        )}
      </Card>
    </Section>
  );
}

/** An entry opened from a link (the Dashboard) that is not among the loaded rows. */
function SelectedEntry({
  id,
  onClose,
  onShowRecord,
}: {
  id: string;
  onClose: () => void;
  onShowRecord: (type: string, entityId: string) => void;
}) {
  const q = useQuery({
    queryKey: ['audit-entry', id],
    queryFn: async () => (await api.get<AuditDetail>(`/admin/audit/${id}`)).data,
    staleTime: Infinity,
    retry: false,
  });
  return (
    <Card style={{ padding: '16px 20px' }}>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
        }}
      >
        <h3 className="crave-audit-dayhead" style={{ margin: 0 }}>
          {q.data ? `Entry from ${shortWhen(q.data.item.local_time)}` : 'Entry'}
        </h3>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </div>
      {q.isError ? (
        <div style={{ marginTop: 12 }}>
          <Notice kind="error">{errorText(q.error, 'Could not load this entry.')}</Notice>
        </div>
      ) : !q.data ? (
        <p className="crave-audit-muted" style={{ margin: '12px 0 0' }}>
          Loading…
        </p>
      ) : (
        <>
          <p style={{ margin: '12px 0 4px' }}>{plainSummary(q.data.item)}</p>
          <p className="crave-audit-muted" style={{ margin: '0 0 12px', fontSize: 14 }}>
            {personLabel(q.data.item.actor)}
          </p>
          <EntryDetail item={q.data.item} id={`entry-${id}`} onShowRecord={onShowRecord} />
        </>
      )}
    </Card>
  );
}
